import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import styles from '../ReassignDonationModal.module.scss'
import { useAddDonationToReassignmentQueueForm } from '../addToReassignmentQueueForm/useAddToReassignmentQueueForm/useAddDonationToReassignmentQueueForm'
import { AddDonationToReassignmentQueueForm } from '../addToReassignmentQueueForm/addToReassignmentQueueForm/addDonationToReassignmentQueueForm'
import { useOrganisationSearchBox } from '../addToReassignmentQueueForm/useOrganisationSearchBox/useOrganisationSearchBox'

import { AddToReassignmentQueueModalProps } from './AddToReassignmentQueueModal.types'
import { useMutation } from '@percent/admin-dashboard/common/hooks'
import { AcknowledgeModal, ActionModal, ConfirmationModal, Modal, Text } from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { isApiError } from '@percent/admin-dashboard/common/utility/isApiError'

export function AddToReassignmentQueueModal({
  open,
  onClose,
  refresh,
  sourceOrganisationName,
  selectedOrganisation,
  selectedDonation
}: AddToReassignmentQueueModalProps) {
  const { t } = useTranslation()
  const { donationsService } = useServices()
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [selectedOrganisationName, setSelectedOrganisationName] = useState<string | null | undefined>(
    selectedOrganisation?.name
  )

  const [successDialogState, setSuccessDialogState] = useState(false)
  const [errorDialogState, setErrorDialogState] = useState(false)

  const [{ isLoading, error }, { apiFunc: addToReassignmentQueueApiFunc }] = useMutation(
    donationsService.addDonationToReassignmentQueue,
    () => setSuccessDialogState(true),
    () => setErrorDialogState(true)
  )

  const { searchResults, query, isLoading: isOrganisationsLoading, setQuery } = useOrganisationSearchBox()

  const { formik, reasons, reasonOptions } = useAddDonationToReassignmentQueueForm({
    selectedOrganisation,
    onFormSubmit: async ({ organisationId, reassignmentKind, reason }) => {
      await addToReassignmentQueueApiFunc({
        donationId: selectedDonation?.id ?? '',
        payload: {
          reason,
          reassignmentKind,
          newOrganisationId: organisationId
        }
      })
    }
  })

  const {
    touched,
    errors,
    setFieldTouched,
    setFieldValue,
    handleChange,
    handleBlur,
    setValues,
    handleSubmit,
    isValid,
    resetForm,
    values
  } = formik

  const handleCloseModal = () => {
    onClose()
    refresh()
    resetForm()
    setSelectedOrganisationName(null)
    setSuccessDialogState(false)
    setShowConfirmationModal(false)
  }

  const handleOnErrorClose = () => {
    setErrorDialogState(false)
  }

  const successOrErrorModal =
    successDialogState && !errorDialogState ? (
      <AcknowledgeModal
        result="positive"
        title={t('dialog.reassignDonation.success.queueTitle')}
        description={
          <Trans
            i18nKey="dialog.reassignDonation.success.description"
            values={{ orgName: selectedOrganisationName }}
            components={{ b: <strong /> }}
          />
        }
        buttonText={t('button.close')}
        handleClose={handleCloseModal}
        viewTestId="success-donation-reassign-modal"
      />
    ) : (
      <AcknowledgeModal
        result="negative"
        title={t('dialog.reassignDonation.error.title')}
        buttonText={t('button.close')}
        handleClose={handleOnErrorClose}
        viewTestId="error-donation-reassign-modal"
      >
        <div className={styles.errorPage}>
          <Text size="medium">
            <Trans
              i18nKey="dialog.reassignDonation.error.description"
              values={{ orgName: selectedOrganisationName }}
              components={{ b: <strong /> }}
            />
          </Text>
          {error && isApiError(error) && (
            <div className={styles.errorDetails}>
              <Text size="small">{error.message}</Text>
              {error.reasons && (
                <ul>
                  {error.reasons.map((reason, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={`reason-${i}`}>
                      <Text size="small">{reason}</Text>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </AcknowledgeModal>
    )

  return (
    <Modal open={open} onClose={handleCloseModal} aria-labelledby="reassign-donations-form-modal">
      {!successDialogState && !errorDialogState ? (
        showConfirmationModal ? (
          <ConfirmationModal
            title={t('dialog.markToReassign.confirmation.title')}
            loading={isLoading}
            primaryButtonText={t('button.addToQueue')}
            primaryBtnTestId="confirm-donation-reassignment"
            secondaryButtonText={t('button.cancel')}
            handleSubmit={handleSubmit}
            handleClose={() => setShowConfirmationModal(false)}
          >
            {selectedOrganisationName ? (
              <Trans
                i18nKey="dialog.markToReassign.confirmation.description"
                values={{ orgA: sourceOrganisationName, orgB: selectedOrganisationName }}
                components={{ b: <strong /> }}
              />
            ) : (
              <Trans
                i18nKey="dialog.markToReassign.confirmation.description.withoutNewOrg"
                values={{ orgA: sourceOrganisationName }}
                components={{ b: <strong /> }}
              />
            )}
          </ConfirmationModal>
        ) : (
          <ActionModal
            title={t('dialog.addToReassignmentQueue.title')}
            primaryButtonText={t('button.addToQueue')}
            secondaryButtonText={t('button.cancel')}
            type="submit"
            variant={isValid ? 'primary' : 'secondary'}
            disabled={!(isValid && !isLoading)}
            loading={isLoading}
            handleClose={handleCloseModal}
            handleSubmit={() => setShowConfirmationModal(true)}
            primaryBtnTestId="reassign-donation-submit-button"
            secondaryBtnTestId="reassign-donation-cancel-button"
          >
            <AddDonationToReassignmentQueueForm
              values={values}
              touched={touched}
              errors={errors}
              onOrganisationChange={(organisationId, organisationName) => {
                setValues({
                  ...values,
                  organisationId
                })
                setSelectedOrganisationName(organisationName)
              }}
              onQueryChange={q => {
                setQuery(q)
                setFieldTouched('organisationId')
              }}
              onReassignedReasonChange={value => {
                setFieldValue('reassignedReason', value)
              }}
              isOrganisationsQueryLoading={isOrganisationsLoading}
              query={query}
              searchResults={searchResults}
              reassignedReasonOptions={reasonOptions}
              onCustomReasonChange={handleChange}
              onCustomReasonBlur={handleBlur}
              reasons={reasons}
            />
          </ActionModal>
        )
      ) : (
        successOrErrorModal
      )}
    </Modal>
  )
}
